import * as fs from 'fs';
import * as path from 'path';
import { EventEmitter } from 'events';

interface InterceptOptions {
    interceptLog?: boolean;
    interceptError?: boolean;
    interceptWarn?: boolean;
    keepOriginal?: boolean;
    useBuffer?: boolean;
}

interface ReadOptions {
    lines?: number | null;
    fromEnd?: boolean;
    asArray?: boolean;
}

interface WatchOptions {
    persistent?: boolean;
    interval?: number;
    fromEnd?: boolean;
}

interface WatchController {
    id: string;
    stop: () => void;
    isWatching: () => boolean;
}

export interface InterceptController {
    restore: () => void;
    commit: () => Promise<boolean>;
    discard: () => number;
    hasBuffer: () => boolean;
    getBufferSize: () => number;
}

interface DeleteResult {
    filePath: string;
    deleted: boolean;
    error: string | null;
}

type LogLevel = 'INFO' | 'ERROR' | 'WARN' | 'DEBUG';

export class FileLogger extends EventEmitter {
    private static watchers = new Map<string, (curr: fs.Stats, prev: fs.Stats) => void>(); // Store listener functions
    private static buffers = new Map<string, string[]>();
    
    /**
     * Escribe un log en el archivo especificado
     * @param filePath - Ruta del archivo de log
     * @param message - Mensaje a escribir
     * @param level - Nivel del log (INFO, ERROR, WARN, DEBUG)
     * @param append - Si debe agregar al final del archivo (default: true)
     */
    static async write(filePath: string, message: string, level: LogLevel = 'INFO', append: boolean = true): Promise<boolean> {
        try {
            // Asegurar que el directorio existe
            const dir = path.dirname(filePath);
            if (!fs.existsSync(dir)) {
                fs.mkdirSync(dir, { recursive: true });
            }
            
            // Formatear el mensaje con timestamp
            const timestamp = new Date().toISOString();
            const formattedMessage = `[${timestamp}] [${level}] ${message}\n`;
            
            // Si existe un buffer para este archivo, agregarlo al buffer en lugar de escribir directamente
            if (FileLogger.buffers.has(filePath)) {
                FileLogger.buffers.get(filePath)!.push(formattedMessage);
                return true;
            }
            
            // Escribir al archivo normalmente
            if (append) {
                await fs.promises.appendFile(filePath, formattedMessage, 'utf8');
            } else {
                await fs.promises.writeFile(filePath, formattedMessage, 'utf8');
            }
            
            return true;
        } catch (error) {
            console.error('Error escribiendo log:', error);
            throw error;
        }
    }
    
    /**
     * Inicia un buffer temporal para un archivo de log
     * @param filePath - Ruta del archivo de log
     */
    static startBuffer(filePath: string): void {
        if (!FileLogger.buffers.has(filePath)) {
            FileLogger.buffers.set(filePath, []);
        }
    }
    
    /**
     * Confirma y escribe todos los logs del buffer al archivo
     * @param filePath - Ruta del archivo de log
     */
    static async commitBuffer(filePath: string): Promise<boolean> {
        const buffer = FileLogger.buffers.get(filePath);
        if (buffer && buffer.length > 0) {
            try {
                // Asegurar que el directorio existe
                const dir = path.dirname(filePath);
                if (!fs.existsSync(dir)) {
                    fs.mkdirSync(dir, { recursive: true });
                }
                
                // Escribir todos los logs del buffer
                const content = buffer.join('');
                await fs.promises.appendFile(filePath, content, 'utf8');
                
                // Limpiar el buffer
                FileLogger.buffers.delete(filePath);
                return true;
            } catch (error) {
                console.error('Error confirmando buffer:', error);
                throw error;
            }
        }
        
        // Limpiar el buffer aunque esté vacío
        FileLogger.buffers.delete(filePath);
        return false;
    }
    
    /**
     * Descarta todos los logs del buffer sin escribirlos
     * @param filePath - Ruta del archivo de log
     */
    static discardBuffer(filePath: string): number {
        if (FileLogger.buffers.has(filePath)) {
            const discardedCount = FileLogger.buffers.get(filePath)!.length;
            FileLogger.buffers.delete(filePath);
            return discardedCount;
        }
        return 0;
    }
    
    /**
     * Verifica si existe un buffer activo para un archivo
     * @param filePath - Ruta del archivo de log
     */
    static hasBuffer(filePath: string): boolean {
        return FileLogger.buffers.has(filePath);
    }
    
    /**
     * Obtiene el número de logs en el buffer
     * @param filePath - Ruta del archivo de log
     */
    static getBufferSize(filePath: string): number {
        const buffer = FileLogger.buffers.get(filePath);
        return buffer ? buffer.length : 0;
    }
    
    /**
     * Intercepta console.log y console.error para escribir a archivo
     * @param filePath - Ruta del archivo de log
     * @param options - Opciones de interceptación
     */
    static interceptConsole(filePath: string, options: InterceptOptions = {}): InterceptController {
        const {
            interceptLog = true,
            interceptError = true,
            interceptWarn = true,
            keepOriginal = true,  // Mantener el comportamiento original de console
            useBuffer = false     // Usar buffer temporal
        } = options;
        
        // Iniciar buffer si se solicita
        if (useBuffer) {
            FileLogger.startBuffer(filePath);
        }
        
        // Guardar referencias originales
        const originalLog = console.log;
        const originalError = console.error;
        const originalWarn = console.warn;
        
        if (interceptLog) {
            console.log = function(...args: any[]) {
                const message = args.map(arg => 
                    typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
                ).join(' ');
                
                FileLogger.write(filePath, message, 'INFO').catch(err => {
                    originalError('Error escribiendo log:', err);
                });
                
                if (keepOriginal) {
                    originalLog.apply(console, args);
                }
            };
        }
        
        if (interceptError) {
            console.error = function(...args: any[]) {
                const message = args.map(arg => 
                    typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
                ).join(' ');
                
                FileLogger.write(filePath, message, 'ERROR').catch(err => {
                    originalError('Error escribiendo error log:', err);
                });
                
                if (keepOriginal) {
                    originalError.apply(console, args);
                }
            };
        }
        
        if (interceptWarn) {
            console.warn = function(...args: any[]) {
                const message = args.map(arg => 
                    typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
                ).join(' ');
                
                FileLogger.write(filePath, message, 'WARN').catch(err => {
                    originalError('Error escribiendo warn log:', err);
                });
                
                if (keepOriginal) {
                    originalWarn.apply(console, args);
                }
            };
        }
        
        // Retornar función para restaurar console original
        return {
            restore: () => {
                if (interceptLog) console.log = originalLog;
                if (interceptError) console.error = originalError;
                if (interceptWarn) console.warn = originalWarn;
            },
            commit: async () => {
                if (useBuffer) {
                    return await FileLogger.commitBuffer(filePath);
                }
                return false;
            },
            discard: () => {
                if (useBuffer) {
                    return FileLogger.discardBuffer(filePath);
                }
                return 0;
            },
            hasBuffer: () => FileLogger.hasBuffer(filePath),
            getBufferSize: () => FileLogger.getBufferSize(filePath)
        };
    }
    
    /**
     * Lee el contenido completo del archivo de log
     * @param filePath - Ruta del archivo de log
     * @param options - Opciones de lectura
     * @returns Contenido del archivo
     */
    static async read(filePath: string, options: ReadOptions = {}): Promise<string | string[]> {
        const { 
            lines = null,      // Número de líneas a leer (null = todas)
            fromEnd = false,   // Si debe leer desde el final
            asArray = false    // Si debe retornar como array de líneas
        } = options;
        
        try {
            if (!fs.existsSync(filePath)) {
                return asArray ? [] : '';
            }
            
            let content = await fs.promises.readFile(filePath, 'utf8');
            
            if (asArray) {
                let linesArray = content.split('\n').filter(line => line.trim() !== '');
                
                if (lines !== null) {
                    if (fromEnd) {
                        linesArray = linesArray.slice(-lines);
                    } else {
                        linesArray = linesArray.slice(0, lines);
                    }
                }
                
                return linesArray;
            }
            
            // Si se especifica número de líneas y no es array
            if (lines !== null) {
                let linesArray = content.split('\n').filter(line => line.trim() !== '');
                if (fromEnd) {
                    linesArray = linesArray.slice(-lines);
                } else {
                    linesArray = linesArray.slice(0, lines);
                }
                content = linesArray.join('\n');
            }
            
            return content;
        } catch (error) {
            console.error('Error leyendo log:', error);
            throw error;
        }
    }
    
    /**
     * Observa un archivo de log en tiempo real
     * @param filePath - Ruta del archivo de log
     * @param callback - Función callback para nuevas líneas
     * @param options - Opciones del watcher
     * @returns Objeto con métodos para controlar el watcher
     */
    static watch(filePath: string, callback: (line: string, filePath: string) => void, options: WatchOptions = {}): WatchController {
        const {
            persistent = true,
            interval = 100,
            fromEnd = true  // Empezar desde el final del archivo
        } = options;
        
        let lastSize = 0;
        let lastPosition = 0;
        
        // Inicializar posición si el archivo ya existe
        if (fs.existsSync(filePath)) {
            const stats = fs.statSync(filePath);
            lastSize = stats.size;
            lastPosition = fromEnd ? stats.size : 0;
        }
        
        // Fixed: Correct listener function signature for fs.watchFile
        const listener = async (curr: fs.Stats, prev: fs.Stats) => {
            try {
                if (curr.size > lastSize) {
                    // El archivo ha crecido
                    const stream = fs.createReadStream(filePath, {
                        start: lastPosition,
                        end: curr.size - 1,
                        encoding: 'utf8'
                    });
                    
                    let buffer = '';
                    
                    stream.on('data', (chunk) => {
                        buffer += chunk;
                        const lines = buffer.split('\n');
                        
                        // Procesar líneas completas
                        for (let i = 0; i < lines.length - 1; i++) {
                            if (lines[i].trim()) {
                                callback(lines[i].trim(), filePath);
                            }
                        }
                        
                        // Guardar la línea incompleta
                        buffer = lines[lines.length - 1];
                    });
                    
                    stream.on('end', () => {
                        // Procesar la última línea si existe
                        if (buffer.trim()) {
                            callback(buffer.trim(), filePath);
                        }
                    });
                    
                    lastSize = curr.size;
                    lastPosition = curr.size;
                }
            } catch (error) {
                console.error('Error en watcher:', error);
            }
        };
        
        // Watcher para cambios en el archivo
        fs.watchFile(filePath, { persistent, interval }, listener);
        
        // Guardar referencia del watcher
        const watcherId = `${filePath}_${Date.now()}`;
        // Store listener reference for cleanup
        FileLogger.watchers.set(watcherId, listener as any);
        
        // Retornar objeto de control
        return {
            id: watcherId,
            stop: () => {
                const storedListener = FileLogger.watchers.get(watcherId);
                if (storedListener) {
                    fs.unwatchFile(filePath, storedListener);
                    FileLogger.watchers.delete(watcherId);
                }
            },
            isWatching: () => FileLogger.watchers.has(watcherId)
        };
    }
    
    /**
     * Detiene todos los watchers activos
     */
    static stopAllWatchers(): void {
        for (const [watcherId] of FileLogger.watchers) {
            const filePath = watcherId.split('_')[0];
            fs.unwatchFile(filePath);
        }
        FileLogger.watchers.clear();
    }
    
    /**
     * Métodos de conveniencia para diferentes niveles de log
     */
    static async info(filePath: string, message: string): Promise<boolean> {
        return this.write(filePath, message, 'INFO');
    }
    
    static async error(filePath: string, message: string): Promise<boolean> {
        return this.write(filePath, message, 'ERROR');
    }
    
    static async warn(filePath: string, message: string): Promise<boolean> {
        return this.write(filePath, message, 'WARN');
    }
    
    static async debug(filePath: string, message: string): Promise<boolean> {
        return this.write(filePath, message, 'DEBUG');
    }
    
    /**
     * Limpia logs antiguos
     * @param filePath - Ruta del archivo de log
     * @param maxLines - Máximo número de líneas a mantener
     */
    static async cleanup(filePath: string, maxLines: number = 1000): Promise<number> {
        try {
            const lines = await this.read(filePath, { asArray: true }) as string[];
            
            if (lines.length > maxLines) {
                const keepLines = lines.slice(-maxLines);
                const content = keepLines.join('\n') + '\n';
                await fs.promises.writeFile(filePath, content, 'utf8');
                return lines.length - maxLines; // Líneas eliminadas
            }
            
            return 0;
        } catch (error) {
            console.error('Error limpiando logs:', error);
            throw error;
        }
    }
    
    /**
     * Elimina un archivo de log
     * @param filePath - Ruta del archivo de log a eliminar
     */
    static async deleteLogFile(filePath: string): Promise<boolean> {
        try {
            if (fs.existsSync(filePath)) {
                await fs.promises.unlink(filePath);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error eliminando archivo de log:', error);
            throw error;
        }
    }
    
    /**
     * Elimina múltiples archivos de log
     * @param filePaths - Array de rutas de archivos de log a eliminar
     */
    static async deleteLogFiles(filePaths: string[]): Promise<DeleteResult[]> {
        const results: DeleteResult[] = [];
        
        for (const filePath of filePaths) {
            try {
                const deleted = await this.deleteLogFile(filePath);
                results.push({ filePath, deleted, error: null });
            } catch (error) {
                results.push({ filePath, deleted: false, error: (error as Error).message });
            }
        }
        
        return results;
    }
}

export default FileLogger;